import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  Users, 
  Search, 
  Edit3, 
  Eye, 
  Lock, 
  Unlock, 
  Trash2, 
  Shield, 
  CheckCircle,
  XCircle,
  Clock,
  UserCheck,
  UserX,
  X,
  Mail,
  Building,
  Phone,
  MapPin,
  AlertCircle
} from 'lucide-react';

interface ClientPortal {
  portal_cliente_id: string;
  client_id: string;
  client_name: string;
  portal_email: string; // Email del portal (auth.users.email)
  client_email: string; // Email del cliente (clients.email)
  client_business_name: string;
  created_at: string;
  created_by: string;
  is_active: boolean;
  sign_in_count: number;
  last_sign_in: string; // Fecha de último acceso
}

interface ClientPortalDetails extends ClientPortal {
  client_phone: string | null;
  client_address: string | null;
  client_city: string | null;
  client_postal_code: string | null;
  client_country: string | null;
  client_tax_id: string | null;
  updated_at: string | null;
  email_confirmed: boolean;
  phone_confirmed: boolean;
}

const ClientPortalManagement: React.FC = () => {
  const { user, userRole } = useAuth();
  const [clientPortals, setClientPortals] = useState<ClientPortal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPortal, setSelectedPortal] = useState<ClientPortalDetails | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    portal_email: '',
    email: '',
    business_name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    tax_id: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (userRole === 'Administrador') {
      loadClientPortals();
    }
  }, [userRole]);

  const loadClientPortals = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Cargando portales de clientes...');
      
      const { data, error } = await supabase.rpc('admin_list_client_portals');
      
      if (error) {
        console.error('❌ Error en RPC:', error);
        throw error;
      }

      console.log('✅ Datos recibidos:', data);
      setClientPortals(data || []);
      
      // Logging inteligente: identificar datos problemáticos
      if (data && data.length > 0) {
        const incompleteData = data.filter(portal => 
          !portal.portal_email || portal.portal_email === 'N/A' ||
          !portal.client_email || portal.client_email === 'N/A' ||
          !portal.client_business_name || portal.client_business_name === 'N/A'
        );
        
        if (incompleteData.length > 0) {
          console.warn(`⚠️ ${incompleteData.length} portales con datos incompletos:`, incompleteData);
        } else {
          console.log('✅ Todos los portales tienen datos completos');
        }
      }
      
      if (!data || data.length === 0) {
        console.log('⚠️ No se encontraron portales de clientes');
        setError('No se encontraron portales de clientes en el sistema');
      }
    } catch (e: any) {
      console.error('💥 Error cargando cuentas de portal:', e);
      setError(`Error: ${e.message || 'No se pudieron cargar las cuentas de portal'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPortalDetails = async (portalClienteId: string) => {
    try {
      const { data, error } = await supabase.rpc('admin_get_client_portal_details', {
        p_portal_cliente_id: portalClienteId
      });
      if (error) throw error;

      if (data && data.length > 0) {
        const details = data[0];
        setSelectedPortal(details);
              setEditForm({
        portal_email: details.portal_email || '',
        email: details.client_email || '',
        business_name: details.client_business_name || '',
        phone: details.client_phone || '',
        address: details.client_address || '',
        city: details.client_city || '',
        postal_code: details.client_postal_code || '',
        country: details.client_country || '',
        tax_id: details.client_tax_id || ''
      });
        setShowDetailsModal(true);
      }
    } catch (e: any) {
      console.error('Error cargando detalles:', e);
      alert('Error al cargar los detalles del portal');
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedPortal) return;

    try {
      setActionLoading(true);

      // Cambiar email del portal si es diferente
      if (editForm.portal_email && editForm.portal_email !== selectedPortal.portal_email) {
        const { error: portalEmailError } = await supabase.rpc('admin_change_client_portal_email', {
          p_portal_cliente_id: selectedPortal.portal_cliente_id,
          p_new_email: editForm.portal_email
        });
        if (portalEmailError) throw portalEmailError;
      }

      // Cambiar email del cliente si es diferente
      if (editForm.email !== selectedPortal.client_email) {
        // Actualizar email en la tabla clients
        const { error: clientEmailError } = await supabase
          .from('clients')
          .update({
            email: editForm.email,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedPortal.client_id);
        
        if (clientEmailError) throw clientEmailError;
      }

      // Actualizar otros campos en la tabla clients
      const { error: updateError } = await supabase
        .from('clients')
        .update({
          business_name: editForm.business_name,
          phone: editForm.phone,
          address: editForm.address,
          city: editForm.city,
          postal_code: editForm.postal_code,
          country: editForm.country,
          cuit: editForm.tax_id,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedPortal.client_id);

      if (updateError) throw updateError;

      alert('Portal actualizado correctamente');
      setShowEditModal(false);
      loadClientPortals(); // Recargar lista
    } catch (e: any) {
      console.error('Error actualizando portal:', e);
      alert(`Error al actualizar: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePasswordChangeModal = async () => {
    if (!selectedPortal) return;

    if (newPassword !== confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      setActionLoading(true);

      const { error } = await supabase.rpc('admin_change_client_portal_password', {
        p_portal_cliente_id: selectedPortal.portal_cliente_id,
        p_new_password: newPassword
      });

      if (error) throw error;

      alert('Contraseña actualizada correctamente');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      console.error('Error cambiando contraseña:', e);
      alert(`Error al cambiar contraseña: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (portalClienteId: string, isActive: boolean) => {
    try {
      setActionLoading(true);

      if (isActive) {
        // Desactivar
        const { error } = await supabase.rpc('admin_deactivate_client_portal', {
          p_portal_cliente_id: portalClienteId
        });
        if (error) throw error;
        alert('Portal desactivado correctamente');
      } else {
        // Reactivar
        const { error } = await supabase.rpc('admin_reactivate_client_portal', {
          p_portal_cliente_id: portalClienteId
        });
        if (error) throw error;
        alert('Portal reactivado correctamente');
      }

      loadClientPortals(); // Recargar lista
    } catch (e: any) {
      console.error('Error cambiando estado:', e);
      alert(`Error al cambiar estado: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePasswordChange = async (portalClienteId: string) => {
    const newPassword = prompt('Ingresa la nueva contraseña (mínimo 6 caracteres):');
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      setActionLoading(true);
      
      const { error } = await supabase.rpc('admin_change_client_portal_password', {
        p_portal_cliente_id: portalClienteId,
        p_new_password: newPassword
      });
      
      if (error) throw error;
      alert('Contraseña cambiada correctamente');
      
    } catch (e: any) {
      console.error('Error cambiando contraseña:', e);
      alert(`Error: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePortalInline = async (portalClienteId: string, clientName: string) => {
    const confirmDelete = confirm(`¿Estás seguro de que quieres eliminar el portal de ${clientName}?\n\nEsta acción NO se puede deshacer.`);
    if (!confirmDelete) return;

    try {
      setActionLoading(true);
      
      const { error } = await supabase.rpc('admin_delete_client_portal', {
        p_portal_cliente_id: portalClienteId
      });
      
      if (error) throw error;
      alert(`Portal de ${clientName} eliminado correctamente`);
      
      loadClientPortals(); // Recargar lista
    } catch (e: any) {
      console.error('Error eliminando portal:', e);
      alert(`Error: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePortal = async () => {
    if (!selectedPortal) return;

    try {
      setActionLoading(true);

      const { error } = await supabase.rpc('admin_delete_client_portal', {
        p_portal_cliente_id: selectedPortal.portal_cliente_id
      });

      if (error) throw error;

      alert('Portal eliminado correctamente');
      setShowDeleteModal(false);
      setShowDetailsModal(false);
      loadClientPortals(); // Recargar lista
    } catch (e: any) {
      console.error('Error eliminando portal:', e);
      alert(`Error al eliminar: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-AR');
  };

  const formatRelativeTime = (dateString: string) => {
    if (!dateString) return 'Nunca';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Hace menos de 1 hora';
    if (diffInHours < 24) return `Hace ${diffInHours} horas`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `Hace ${diffInDays} días`;
    
    return formatDate(dateString);
  };

  // Verificar acceso de administrador
  if (userRole !== 'Administrador') {
    return (
      <div className="text-center py-12">
        <Shield className="mx-auto h-12 w-12 text-red-500" />
        <h3 className="mt-2 text-lg font-medium text-gray-900">Acceso Denegado</h3>
        <p className="mt-1 text-gray-600">Solo los administradores pueden acceder a esta página.</p>
      </div>
    );
  }

  // Filtrar cuentas
  const filteredPortals = clientPortals.filter(portal =>
    portal.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    portal.portal_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    portal.client_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (portal.client_business_name && portal.client_business_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Administración de Portales de Clientes</h1>
        <p className="text-gray-600">Gestiona las cuentas de portal de clientes del sistema</p>
        
        {/* Debug Info */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-medium text-blue-900 mb-2">🔍 Información de Debug:</h3>
          <div className="text-sm text-blue-700 space-y-1">
            <div>Usuario ID: {user?.id || 'N/A'}</div>
            <div>Rol: {userRole || 'N/A'}</div>
            <div>Es Admin: {userRole === 'Administrador' ? '✅ Sí' : '❌ No'}</div>
            <div>Email: {user?.email || 'N/A'}</div>
          </div>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Portales</p>
              <p className="text-2xl font-bold text-gray-900">{clientPortals.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <UserCheck className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Portales Activos</p>
              <p className="text-2xl font-bold text-gray-900">
                {clientPortals.filter(p => p.is_active).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <UserX className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Portales Inactivos</p>
              <p className="text-2xl font-bold text-gray-900">
                {clientPortals.filter(p => !p.is_active).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Último Portal</p>
              <p className="text-sm font-bold text-gray-900">
                {clientPortals.length > 0 ? formatRelativeTime(clientPortals[0].created_at) : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Búsqueda y Debug */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, email portal, email cliente o empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
        </div>
        
        {/* Botón de Debug */}
        <button
          onClick={loadClientPortals}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          🔄 Recargar
        </button>
        
        {/* Botón de Debug RPC */}
        <button
          onClick={async () => {
            try {
              console.log('🧪 Probando función RPC optimizada...');
              const { data, error } = await supabase.rpc('admin_list_client_portals');
              
              if (error) {
                console.error('❌ Error RPC:', error);
                alert(`Error RPC: ${error.message}`);
              } else {
                console.log('✅ RPC exitoso:', data);
                
                // Logging inteligente: solo mostrar datos problemáticos
                if (data && data.length > 0) {
                  const problematicData = data.filter(portal => 
                    !portal.portal_email || portal.portal_email === 'N/A' ||
                    !portal.client_email || portal.client_email === 'N/A' ||
                    !portal.client_business_name || portal.client_business_name === 'N/A'
                  );
                  
                  if (problematicData.length > 0) {
                    console.warn('⚠️ Datos problemáticos encontrados:', problematicData);
                    alert(`⚠️ ${problematicData.length} portales con datos incompletos. Revisa la consola.`);
                  } else {
                    alert(`✅ Todos los ${data.length} portales tienen datos completos`);
                  }
                }
              }
            } catch (e: any) {
              console.error('❌ Error en test RPC:', e);
              alert(`Error en test: ${e.message}`);
            }
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          🧪 Test RPC Optimizado
        </button>
        
        <button
          onClick={async () => {
            try {
              console.log('🔍 Ejecutando diagnóstico simple...');
              
              // Usar la función simplificada
              const { data: diagnoseData, error: diagnoseError } = await supabase.rpc('diagnose_portal_simple');
              if (diagnoseError) throw diagnoseError;
              
              console.log('🔍 Diagnóstico simple:', diagnoseData);
              
              // Mostrar resumen en alert
              if (diagnoseData && diagnoseData.length > 0) {
                const totalClients = diagnoseData.length;
                const withPortal = diagnoseData.filter(d => d.has_portal).length;
                const withoutPortal = diagnoseData.filter(d => !d.has_portal).length;
                
                alert(`🔍 Diagnóstico:\n\nTotal clientes: ${totalClients}\nCon portal: ${withPortal}\nSin portal: ${withoutPortal}\n\nRevisa la consola para detalles.`);
              } else {
                alert('🔍 No se encontraron clientes en el sistema.');
              }
              
            } catch (e: any) {
              console.error('❌ Error en diagnóstico:', e);
              alert(`Error en diagnóstico: ${e.message}`);
            }
          }}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          🔍 Diagnóstico Simple
        </button>
        
        <button
          onClick={async () => {
            try {
              console.log('🔍 Debug específico ANIMAL y SHAKA...');
              
              // Usar la función más simple y confiable
              const { data: debugData, error: debugError } = await supabase.rpc('debug_portal_data_simple');
              if (debugError) throw debugError;
              
              console.log('🔍 Debug ANIMAL y SHAKA:', debugData);
              
              // Mostrar resumen en alert
              if (debugData && debugData.length > 0) {
                const summary = debugData.map(d => 
                  `${d.client_name}:\n  Portal ID: ${d.portal_cliente_id}\n  Email Cliente: ${d.client_email}\n  Empresa: ${d.client_business_name}\n  Email Portal: ${d.portal_email}`
                ).join('\n\n');
                
                alert(`🔍 Debug ANIMAL y SHAKA:\n\n${summary}\n\nRevisa la consola para detalles completos.`);
              } else {
                alert('🔍 No se encontraron datos de ANIMAL y SHAKA.');
              }
              
            } catch (e: any) {
              console.error('❌ Error en debug específico:', e);
              alert(`Error en debug: ${e.message}`);
            }
          }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          🔍 Debug ANIMAL/SHAKA
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Tabla de Portales */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Portal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creado Por
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Accesos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último Acceso
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPortals.map((portal) => (
                <tr key={portal.portal_cliente_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {portal.client_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{portal.client_name}</div>
                        <div className="text-sm text-gray-500">ID: {portal.client_id.slice(0, 8)}...</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{portal.portal_email || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{portal.client_email || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{portal.client_business_name || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      portal.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {portal.is_active ? (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Activo
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3 mr-1" />
                          Inactivo
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {portal.created_by}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{portal.sign_in_count || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {portal.last_sign_in ? new Date(portal.last_sign_in).toLocaleDateString('es-ES') : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={() => loadPortalDetails(portal.portal_cliente_id)}
                        className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Ver detalles"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => loadPortalDetails(portal.portal_cliente_id)}
                        className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => handlePasswordChange(portal.portal_cliente_id)}
                        className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Cambiar Contraseña"
                      >
                        <Lock className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => handleToggleStatus(portal.portal_cliente_id, portal.is_active)}
                        disabled={actionLoading}
                        className={`p-2 rounded-lg transition-colors ${
                          portal.is_active
                            ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'
                            : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                        }`}
                        title={portal.is_active ? 'Desactivar' : 'Reactivar'}
                      >
                        {portal.is_active ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                      </button>
                      
                      <button
                        onClick={() => handleDeletePortal(portal.portal_cliente_id, portal.client_name)}
                        disabled={actionLoading}
                        className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar Portal"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Detalles */}
      {showDetailsModal && selectedPortal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Detalles del Portal</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Información del Cliente</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Nombre</label>
                    <p className="text-sm text-gray-900">{selectedPortal.client_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Email del Portal</label>
                    <p className="text-sm text-gray-900">{selectedPortal.portal_email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Email del Cliente</label>
                    <p className="text-sm text-gray-900">{selectedPortal.client_email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Empresa</label>
                    <p className="text-sm text-gray-900">{selectedPortal.client_business_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Teléfono</label>
                    <p className="text-sm text-gray-900">{selectedPortal.client_phone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Información del Portal</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Estado</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedPortal.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedPortal.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Creado</label>
                    <p className="text-sm text-gray-900">{formatDate(selectedPortal.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Creado Por</label>
                    <p className="text-sm text-gray-900">{selectedPortal.created_by}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Total de Accesos</label>
                    <p className="text-sm text-gray-900">{selectedPortal.sign_in_count || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Último Acceso</label>
                    <p className="text-sm text-gray-900">
                      {selectedPortal.last_sign_in ? new Date(selectedPortal.last_sign_in).toLocaleDateString('es-ES') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setShowEditModal(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Editar
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setShowPasswordModal(true);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Cambiar Contraseña
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setShowDeleteModal(true);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición */}
      {showEditModal && selectedPortal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Editar Portal</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email del Portal</label>
                <input
                  type="email"
                  value={editForm.portal_email || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, portal_email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Email para acceso al portal"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email del Cliente</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Email de contacto del cliente"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
                <input
                  type="text"
                  value={editForm.business_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, business_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                <input
                  type="text"
                  value={editForm.address}
                  onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ciudad</label>
                <input
                  type="text"
                  value={editForm.city}
                  onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Código Postal</label>
                <input
                  type="text"
                  value={editForm.postal_code}
                  onChange={(e) => setEditForm(prev => ({ ...prev, postal_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">País</label>
                <input
                  type="text"
                  value={editForm.country}
                  onChange={(e) => setEditForm(prev => ({ ...prev, country: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CUIT/CUIL</label>
                <input
                  type="text"
                  value={editForm.tax_id}
                  onChange={(e) => setEditForm(prev => ({ ...prev, tax_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cambio de Contraseña */}
      {showPasswordModal && selectedPortal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Cambiar Contraseña</h2>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nueva Contraseña</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Contraseña</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Repite la contraseña"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handlePasswordChangeModal}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Actualizando...' : 'Actualizar Contraseña'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {showDeleteModal && selectedPortal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-red-900">Confirmar Eliminación</h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="text-center">
              <Trash2 className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                ¿Eliminar portal de {selectedPortal.client_name}?
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Esta acción eliminará permanentemente la cuenta de portal del cliente. 
                El cliente no podrá acceder al sistema hasta que se cree una nueva cuenta.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeletePortal}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Eliminando...' : 'Eliminar Portal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resumen de resultados */}
      {filteredPortals.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-500">
          Mostrando {filteredPortals.length} de {clientPortals.length} portales
          {searchTerm && ` que coinciden con "${searchTerm}"`}
        </div>
      )}

      {filteredPortals.length === 0 && searchTerm && (
        <div className="mt-6 text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No se encontraron portales</h3>
          <p className="mt-1 text-sm text-gray-600">
            No hay portales que coincidan con "{searchTerm}"
          </p>
        </div>
      )}
    </div>
  );
};

export default ClientPortalManagement;
